import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";

import { Avatar } from "../../components/ui/Avatar";
import { useChatDock } from "../../stores/chat";
import type { Conversation } from "./api";

/**
 * The way into the dock when nothing is docked yet.
 *
 * ## Why this exists
 *
 * The rail started out rendering only when it had a tab in it, which is
 * circular: the only things that docked a conversation were a profile's
 * "Message" button and this - which was not there. So a fresh page load showed
 * a dock with no chat in it and no way to put chat into it, and the feature was
 * invisible unless you happened to walk past somebody's profile first.
 *
 * So the launcher is always present for a signed-in desktop viewer, and the
 * conversation list hangs off it. It is the one part of the rail that does not
 * depend on state, which is what makes the rest of it reachable.
 *
 * ## It lists, it does not converse
 *
 * Picking somebody here docks them and opens their window - the same thing the
 * Message button does. The panel is a directory, so it closes on the way
 * through rather than becoming a second place to read messages in.
 */
const WIDTH = 320;
const HEIGHT = 400;

export function ChatLauncher({
  conversations,
  loading,
}: {
  conversations: Conversation[];
  loading: boolean;
}) {
  const [open, setOpen] = useState(false);
  const dock = useChatDock((state) => state.start);
  const docked = useChatDock((state) => state.tabs);
  const root = useRef<HTMLDivElement | null>(null);

  /*
   * Escape, and a click anywhere else.
   *
   * `pointerdown` rather than `click`: a panel that closes on click-up stays
   * open under the press that dismissed it, which reads as the dismissal not
   * having worked.
   */
  useEffect(() => {
    if (!open) return;

    const away = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };

    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", key);

    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", key);
    };
  }, [open]);

  /*
   * Only what is not already on screen.
   *
   * A conversation open in a window beside this has been read, so counting it
   * would badge the launcher for messages the viewer is looking at.
   */
  const unread = conversations
    .filter((item) => !docked.includes(item.id))
    .reduce((total, item) => total + item.unread, 0);

  return (
    <div className="relative flex h-full items-stretch border-l border-line" ref={root}>
      {open && (
        <div
          style={{ width: WIDTH, maxHeight: HEIGHT }}
          data-chat-launcher-panel
          className="absolute bottom-full right-0 z-10 flex flex-col overflow-hidden rounded-t-[var(--radius-panel)] border border-b-0 border-line-lit bg-raised shadow-2xl"
        >
          <div className="flex shrink-0 items-center justify-between border-b border-line px-3 py-2">
            <span className="text-sm font-medium text-ink">Messages</span>

            <Link
              to="/messages"
              onClick={() => setOpen(false)}
              className="meta-caps text-soft transition-colors hover:text-ink"
            >
              See all
            </Link>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
            {loading && <p className="px-2 py-3 font-mono text-[10px] text-ghost">loading…</p>}

            {!loading && conversations.length === 0 && (
              <p className="px-2 py-3 text-xs text-faint">
                Nothing here yet. Open somebody's profile and press Message to start one.
              </p>
            )}

            {conversations.map((item) => {
              const name =
                item.other?.display_name || item.other?.username || item.title || "Conversation";

              return (
                <button
                  key={item.id}
                  type="button"
                  data-chat-launcher-row={item.id}
                  onClick={() => {
                    dock(item.id, item.other);
                    setOpen(false);
                  }}
                  className="flex w-full items-center gap-2.5 rounded-[var(--radius-control)] px-2 py-2 text-left transition-colors hover:bg-slate"
                >
                  <Avatar src={item.other?.avatar ?? null} name={name} size={32} />

                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium text-ink">{name}</span>
                    <span
                      className={`block truncate text-[11px] ${
                        item.unread > 0 ? "font-medium text-soft" : "text-faint"
                      }`}
                    >
                      {item.preview || "No messages yet"}
                    </span>
                  </span>

                  {item.unread > 0 && (
                    <span className="shrink-0 rounded-[var(--radius-pill)] bg-accent px-1.5 font-mono text-[10px] text-accent-ink">
                      {item.unread > 9 ? "9+" : item.unread}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((was) => !was)}
        aria-expanded={open}
        aria-label="Messages"
        data-chat-launcher
        className={`flex h-full items-center gap-2 px-5 transition-colors ${
          open ? "bg-raised text-ink" : "text-soft hover:bg-raised hover:text-ink"
        }`}
      >
        <svg viewBox="0 0 16 16" className="h-4 w-4" fill="currentColor" aria-hidden>
          <path d="M2 3h12a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Zm.6 1.6L8 8.4l5.4-3.8V4H2.6v.6Z" />
        </svg>

        <span className="text-sm font-medium">Messages</span>

        {unread > 0 && (
          <span
            data-chat-launcher-unread
            className="shrink-0 rounded-[var(--radius-pill)] bg-accent px-1.5 font-mono text-[10px] text-accent-ink"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
    </div>
  );
}
